import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../../contexts/AuthContext';
import { confirmDialog, errorAlert, successAlert } from '../../../helpers/alerts';

// Cadastro dos equipamentos: DVR, NVR e camera IP avulsa (ex.: Intelbras
// Mibo, que e um "equipamento" de um canal so). A senha so vai daqui para o
// backend — nunca volta: a lista traz apenas `senha_definida`.

const TIPOS = [
  { id: 'dvr', rotulo: 'DVR' },
  { id: 'nvr', rotulo: 'NVR' },
  { id: 'camera', rotulo: 'Câmera IP (sem DVR, ex.: Mibo)' },
];

const FORM_VAZIO = {
  id: null,
  nome: '',
  tipo: 'dvr',
  filial: '',
  host: '',
  porta: 554,
  porta_http: 80,
  usuario: 'admin',
  senha: '',
  canais: '1-16',
  subtipo: 1,
  por_tela: 16,
  caminho: '',
  caminho_playback: '',
  ativo: true,
  ordem: 0,
};

export default function EquipamentosAba({ onAlterou }) {
  const [gravadores, setGravadores] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [opcoes, setOpcoes] = useState({ grades: [1, 4, 9, 16], caminho_padrao: '', caminho_playback_padrao: '' });
  const [form, setForm] = useState(null); // null = lista; objeto = formulario
  const [avancado, setAvancado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [acoes, setAcoes] = useState({}); // id -> { teste, importando }

  const carregar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cameras/gravadores`);
      const data = res.data?.data || {};
      setGravadores(data.gravadores || []);
      setOpcoes({
        grades: data.grades?.length ? data.grades : [1, 4, 9, 16],
        caminho_padrao: data.caminho_padrao || '',
        caminho_playback_padrao: data.caminho_playback_padrao || '',
      });
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Não foi possível carregar os equipamentos.');
    }
  }, []);

  useEffect(() => {
    carregar();
    axios.get(`${API}/cameras/filiais`)
      .then((res) => setFiliais(res.data?.data || []))
      .catch(() => setFiliais([]));
  }, [carregar]);

  const setCampo = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const setAcao = (id, dados) => setAcoes((a) => ({ ...a, [id]: { ...a[id], ...dados } }));

  const abrir = (g) => {
    setErro('');
    setAvancado(!!(g?.caminho || g?.caminho_playback));
    setForm(g ? {
      ...FORM_VAZIO, ...g, senha: '', filial: g.filial || '', caminho: g.caminho || '', caminho_playback: g.caminho_playback || '',
    } : { ...FORM_VAZIO });
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErro('');
    try {
      const corpo = {
        ...form,
        porta: Number(form.porta),
        porta_http: Number(form.porta_http),
        subtipo: Number(form.subtipo),
        por_tela: Number(form.por_tela),
        ordem: Number(form.ordem) || 0,
      };
      if (form.id) await axios.put(`${API}/cameras/gravadores/${form.id}`, corpo);
      else await axios.post(`${API}/cameras/gravadores`, corpo);
      setForm(null);
      await carregar();
      onAlterou();
    } catch (err) {
      setErro(err.response?.data?.message || 'Não foi possível salvar o equipamento.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (g) => {
    const r = await confirmDialog({
      title: `Excluir "${g.nome}"?`,
      text: 'As câmeras dele saem do portal e dos tours. As gravações no equipamento não são afetadas.',
      confirmButtonText: 'Excluir',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/cameras/gravadores/${g.id}`);
      await carregar();
      onAlterou();
    } catch (err) {
      errorAlert({ title: 'Erro', text: err.response?.data?.message || 'Não foi possível excluir.' });
    }
  };

  const testar = async (g) => {
    setAcao(g.id, { teste: 'testando' });
    try {
      const res = await axios.post(`${API}/cameras/gravadores/${g.id}/testar`);
      setAcao(g.id, { teste: res.data?.data });
    } catch (err) {
      setAcao(g.id, { teste: { ok: false, erro: err.response?.data?.message || 'Falha no teste' } });
    }
  };

  const importarNomes = async (g) => {
    const r = await confirmDialog({
      title: `Importar os nomes das câmeras de "${g.nome}"?`,
      text: 'Os nomes cadastrados no equipamento entram nas câmeras que ainda não têm nome no portal. Nomes já digitados aqui são mantidos.',
      confirmButtonText: 'Importar',
    });
    if (!r.isConfirmed) return;
    setAcao(g.id, { importando: true });
    try {
      const res = await axios.post(`${API}/cameras/gravadores/${g.id}/importar-nomes`, { sobrescrever: false });
      const { encontrados, atualizadas } = res.data?.data || {};
      successAlert({
        title: 'Nomes importados',
        text: `${encontrados} nome(s) encontrados no equipamento; ${atualizadas} câmera(s) atualizadas.`,
      });
      onAlterou();
    } catch (err) {
      errorAlert({ title: 'Não foi possível importar', text: err.response?.data?.message || 'Falha ao falar com o equipamento.' });
    } finally {
      setAcao(g.id, { importando: false });
    }
  };

  const resultadoTeste = (id) => {
    const t = acoes[id]?.teste;
    if (!t) return null;
    if (t === 'testando') return <small className="cell-muted">Testando…</small>;
    return t.ok
      ? <small style={{ color: 'var(--portal-green-dark)' }}>Servidor alcança ({t.ms} ms)</small>
      : <small style={{ color: '#ff8b8b' }}>Servidor NÃO alcança: {t.erro}</small>;
  };

  if (form) {
    const ehCamera = form.tipo === 'camera';
    return (
      <form onSubmit={salvar} className="cameras-form">
        {erro ? <div className="feedback-banner feedback-banner--error cameras-form__largo">{erro}</div> : null}
        <label className="portal-field cameras-form__largo">
          <span>Nome</span>
          <input
            type="text"
            value={form.nome}
            maxLength={100}
            placeholder={ehCamera ? 'Ex.: Recepção - Matriz' : 'Ex.: Loja 03 - DVR'}
            onChange={(e) => setCampo('nome', e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className="portal-field">
          <span>Tipo</span>
          <select value={form.tipo} onChange={(e) => setCampo('tipo', e.target.value)}>
            {TIPOS.map((t) => <option key={t.id} value={t.id}>{t.rotulo}</option>)}
          </select>
        </label>
        <label className="portal-field">
          <span>Filial</span>
          <select value={form.filial} onChange={(e) => setCampo('filial', e.target.value)}>
            <option value="">— Nenhuma —</option>
            {filiais.map((f) => <option key={f.codigo} value={f.codigo}>{f.codigo} - {f.nome}</option>)}
            {form.filial && !filiais.some((f) => f.codigo === form.filial) && (
              <option value={form.filial}>{form.filial}</option>
            )}
          </select>
        </label>
        <label className="portal-field">
          <span>Endereço (IP)</span>
          <input
            type="text"
            value={form.host}
            placeholder="172.16.0.10"
            onChange={(e) => setCampo('host', e.target.value.trim())}
            required
          />
        </label>
        <label className="portal-field">
          <span>Porta RTSP</span>
          <input type="number" min={1} max={65535} value={form.porta} onChange={(e) => setCampo('porta', e.target.value)} required />
        </label>
        <label className="portal-field">
          <span>Usuário</span>
          <input type="text" value={form.usuario} autoComplete="off" onChange={(e) => setCampo('usuario', e.target.value)} required />
        </label>
        <label className="portal-field">
          <span>Senha{form.id ? ' (em branco mantém a atual)' : ''}</span>
          <input
            type="password"
            value={form.senha}
            autoComplete="new-password"
            placeholder={ehCamera ? 'Senha ou código de segurança da etiqueta' : ''}
            onChange={(e) => setCampo('senha', e.target.value)}
            required={!form.id}
          />
        </label>
        {!ehCamera && (
          <>
            <label className="portal-field">
              <span>Canais</span>
              <input
                type="text"
                value={form.canais}
                placeholder="1-16 ou 1-8,10,12"
                onChange={(e) => setCampo('canais', e.target.value)}
                required
              />
            </label>
            <label className="portal-field">
              <span>Câmeras por tela (no tour “Todos”)</span>
              <select value={form.por_tela} onChange={(e) => setCampo('por_tela', e.target.value)}>
                {opcoes.grades.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
          </>
        )}
        <label className="portal-field">
          <span>Stream do mosaico</span>
          <select value={form.subtipo} onChange={(e) => setCampo('subtipo', e.target.value)}>
            <option value={1}>Extra (leve — recomendado)</option>
            <option value={0}>Principal (pesado)</option>
          </select>
        </label>
        <label className="portal-field">
          <span>Ordem</span>
          <input type="number" value={form.ordem} onChange={(e) => setCampo('ordem', e.target.value)} />
        </label>
        <label className="portal-field cameras-form__check">
          <input type="checkbox" checked={!!form.ativo} onChange={(e) => setCampo('ativo', e.target.checked)} />
          <span>Exibir no portal</span>
        </label>

        <button type="button" className="btn btn-link btn-sm cameras-form__largo" style={{ textAlign: 'left', padding: 0 }} onClick={() => setAvancado((v) => !v)}>
          <i className={`bi ${avancado ? 'bi-chevron-down' : 'bi-chevron-right'}`} /> Avançado (só se o equipamento fugir do padrão Intelbras)
        </button>
        {avancado && (
          <>
            <label className="portal-field">
              <span>Porta HTTP (importar nomes)</span>
              <input type="number" min={1} max={65535} value={form.porta_http} onChange={(e) => setCampo('porta_http', e.target.value)} />
            </label>
            <span />
            <label className="portal-field cameras-form__largo">
              <span>Caminho RTSP ao vivo</span>
              <input type="text" value={form.caminho} placeholder={opcoes.caminho_padrao} onChange={(e) => setCampo('caminho', e.target.value)} />
            </label>
            <label className="portal-field cameras-form__largo">
              <span>Caminho RTSP de gravação</span>
              <input
                type="text"
                value={form.caminho_playback}
                placeholder={opcoes.caminho_playback_padrao}
                onChange={(e) => setCampo('caminho_playback', e.target.value)}
              />
            </label>
          </>
        )}

        <div className="cameras-form__largo" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm(null)} disabled={salvando}>
            Voltar
          </button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={salvando}>
            <i className="bi bi-save me-1" />
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    );
  }

  return (
    <>
      {erro ? <div className="feedback-banner feedback-banner--error" style={{ marginBottom: 12 }}>{erro}</div> : null}
      <div className="cameras-config__topo">
        <small className="cell-muted">
          DVRs, NVRs e câmeras IP sem DVR. Cada canal vira uma câmera (nome e grupo na aba Câmeras).
        </small>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => abrir(null)}>
          <i className="bi bi-plus-lg me-1" /> Novo equipamento
        </button>
      </div>

      {gravadores.length === 0 ? (
        <div className="empty-state">
          <i className="bi bi-camera-video" style={{ fontSize: 32, opacity: 0.4 }} />
          <strong>Nenhum equipamento cadastrado</strong>
          <span>Cadastre o primeiro DVR, NVR ou câmera IP.</span>
        </div>
      ) : (
        <div className="table-shell">
          <table className="portal-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Endereço</th>
                <th>Canais</th>
                <th>Portal</th>
                <th style={{ width: 290 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {gravadores.map((g) => (
                <tr key={g.id} style={g.ativo ? undefined : { opacity: 0.55 }}>
                  <td>
                    <div className="cell-strong">{g.nome}</div>
                    <small className="cell-muted">
                      {TIPOS.find((t) => t.id === g.tipo)?.rotulo.split(' (')[0] || g.tipo}
                      {g.filial ? ` · Filial ${g.filial}` : ''}
                    </small>
                  </td>
                  <td>
                    <span style={{ fontFamily: 'monospace' }}>{g.host}:{g.porta}</span>
                    <div>{resultadoTeste(g.id)}</div>
                  </td>
                  <td>{g.tipo === 'camera' ? '—' : g.canais}</td>
                  <td>
                    <span className={`permissao-chip ${g.ativo ? 'permissao-chip--on' : ''}`}>
                      {g.ativo ? 'Exibindo' : 'Oculto'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        title="Testa se o servidor do portal alcança a porta RTSP"
                        disabled={acoes[g.id]?.teste === 'testando'}
                        onClick={() => testar(g)}
                      >
                        <i className="bi bi-reception-4" /> Testar
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        title="Busca no equipamento os nomes das câmeras"
                        disabled={acoes[g.id]?.importando}
                        onClick={() => importarNomes(g)}
                      >
                        <i className="bi bi-download" /> {acoes[g.id]?.importando ? 'Importando…' : 'Nomes'}
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" title="Editar" onClick={() => abrir(g)}>
                        <i className="bi bi-pencil" />
                      </button>
                      <button type="button" className="btn btn-outline-danger btn-sm" title="Excluir" onClick={() => excluir(g)}>
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
